import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Lock, Sparkles } from "lucide-react";

const Section = ({ title, children, testid }) => (
  <section className="mt-8" data-testid={testid}>
    <h2 className="font-cabinet font-bold text-base md:text-lg mb-3">{title}</h2>
    {children}
  </section>
);

// Rule-based labels carry a stable key → interface-language label; unknown keys fall back to the canonical label.
const labelText = (t, item) => {
  if (!item.label_key) return item.label;
  const k = `wordExplorer.labels.${item.label_key}`;
  const v = t(k);
  return v && v !== k ? v : item.label;
};

// Fixed grammatical vocabulary (part of speech, family category) → interface language; unknown → raw value.
export const fixedLabel = (t, group, value) => {
  if (!value) return value;
  const k = `wordExplorer.${group}.${value}`;
  const v = t(k);
  return v && v !== k ? v : value;
};

export function FormsSection({ forms, dir, t }) {
  const [open, setOpen] = useState(false);
  if (!forms?.groups?.length) return null;
  const Group = ({ g }) => (
    <div className="nb-card p-4 bg-white" data-testid="wx-form-group">
      <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2" data-testid="wx-form-group-label">{labelText(t, g)}</div>
      <ul className="space-y-1">
        {g.rows.map((r, i) => (
          <li key={i} className="flex gap-3 text-sm">
            {r.label && <span className="text-gray-500 min-w-[72px]">{labelText(t, r)}</span>}
            <span className="font-bold" dir={dir}>{r.form}</span>
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <Section title={t("wordExplorer.howChanges")} testid="wx-forms">
      <div className="grid sm:grid-cols-2 gap-3">{forms.groups.map((g, i) => <Group key={i} g={g} />)}</div>
      {forms.more_groups?.length > 0 && (
        <>
          <button onClick={() => setOpen(!open)} className="mt-3 text-sm font-bold flex items-center gap-1 hover:underline" data-testid="wx-more-forms-btn">
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /> {t("wordExplorer.seeMoreForms")}
          </button>
          {open && <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="wx-more-forms">{forms.more_groups.map((g, i) => <Group key={i} g={g} />)}</div>}
        </>
      )}
      {forms.source === "ai" && <p className="text-[11px] text-gray-400 mt-2" title={t("wordExplorer.aiAssistedHelp")} data-testid="wx-ai-assisted">{t("wordExplorer.aiAssisted")}</p>}
    </Section>
  );
}

export function FamilySection({ family, dir, t }) {
  if (!family?.length) return null;
  return (
    <Section title={t("wordExplorer.family")} testid="wx-family">
      <div className="flex flex-wrap gap-2">
        {family.map((f, i) => (
          <div key={i} className="nb-card px-3 py-2 bg-white text-sm" data-testid="wx-family-item">
            <span className="text-[10px] uppercase text-gray-400 mr-2" data-testid="wx-family-cat">{fixedLabel(t, "pos", f.category)}</span>
            <span className="font-bold" dir={dir}>{f.word}</span>
            {f.meaning && <span className="text-gray-500"> · {f.meaning}</span>}
          </div>
        ))}
      </div>
    </Section>
  );
}

export function UsageSection({ usage, dir, t }) {
  if (!usage?.length) return null;
  return (
    <Section title={t("wordExplorer.usage")} testid="wx-usage">
      <ul className="space-y-2">
        {usage.map((u, i) => (
          <li key={i} className="border-l-4 border-coral pl-3" data-testid="wx-usage-item">
            <div className="font-bold" dir={dir}>{u.expression}</div>
            <div className="text-sm text-gray-600">{u.meaning}{u.note && <span className="text-gray-400"> — {u.note}</span>}</div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function TrapsSection({ traps, t }) {
  if (!traps?.length) return null;
  return (
    <Section title={t("wordExplorer.traps")} testid="wx-traps">
      <ul className="space-y-1 text-sm text-gray-700 list-disc pl-5">{traps.map((x, i) => <li key={i}>{x.text}</li>)}</ul>
    </Section>
  );
}

export function ExamplesSection({ examples, dir, t }) {
  if (!examples?.length) return null;
  return (
    <Section title={t("wordExplorer.examples")} testid="wx-examples">
      <ul className="space-y-3">
        {examples.map((e, i) => (
          <li key={i} data-testid="wx-example">
            <div className="font-medium" dir={dir}>{e.target}</div>
            <div className="text-sm text-gray-500">{e.native}</div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function ProLock({ t }) {
  return (
    <div className="nb-card bg-butter p-6 mt-8" data-testid="wx-pro-lock">
      <div className="font-cabinet font-bold flex items-center gap-2"><Lock className="w-4 h-4" /> {t("wordExplorer.unlock")}</div>
      <p className="text-sm text-gray-700 mt-1">{t("wordExplorer.unlockSub")}</p>
      <Link to="/billing" className="nb-btn-primary inline-flex mt-4 px-4 py-2 text-sm" data-testid="wx-upgrade-btn"><Sparkles className="w-4 h-4" /> {t("wordExplorer.unlockCta")}</Link>
    </div>
  );
}
